/**
 * Job Scheduler Engine Configuration
 *
 * Source of truth: the Electron provider registry exposed via
 * `job-scheduler:supported-engines` IPC. Call `initializeEngineSupport()`
 * at app startup; after that every helper reads the cached result.
 *
 * To add a new engine, register a provider on the backend — no
 * frontend file changes required.
 */

import { useSyncExternalStore } from 'react';
import type { DatabaseEngineId } from '@/core/engines';
import type { JobSchedulerSupportedEngine } from '@/types/electronJobScheduler';
import { useConnectionStore } from '../../connections/store/connectionStore';

// ── module-level cache ────────────────────────────────────────────────
let cachedEngines: readonly JobSchedulerSupportedEngine[] = [];
let initialized = false;
let supportVersion = 0;
const listeners = new Set<() => void>();
const BUILT_IN_ENGINES: readonly JobSchedulerSupportedEngine[] = [
  { engine: 'mssql', explorerLabel: 'SQL Server Agent', toolbarLabel: 'Agent Jobs' },
  { engine: 'mock', explorerLabel: 'SQL Server Agent', toolbarLabel: 'Agent Jobs' },
];

function notifyListeners(): void {
  supportVersion += 1;
  listeners.forEach((listener) => listener());
}

function setCachedEngines(engines: readonly JobSchedulerSupportedEngine[]): void {
  cachedEngines = engines;
  notifyListeners();
}

/**
 * Fetch supported engines from the Electron provider registry.
 * Safe to call multiple times — only the first call triggers IPC.
 */
export async function initializeEngineSupport(): Promise<void> {
  if (initialized) return;
  try {
    const result = await window.electronAPI?.jobScheduler?.getSupportedEngines();
    if (result?.success) {
      setCachedEngines(result.engines);
    }
  } catch {
    // Electron API unavailable (browser mode) — keep empty
  }
  initialized = true;
}

/**
 * Resolve engine, defaulting undefined to 'mssql' (legacy connections
 * created before the engine field was added are MSSQL).
 */
function resolveEngine(engine: DatabaseEngineId | string | undefined): string {
  return engine ?? 'mssql';
}

/** Get the config for an engine, or undefined if it has no job-scheduler support. */
export function getJobSchedulerEngineConfig(
  engine: DatabaseEngineId | string | undefined,
): JobSchedulerSupportedEngine | undefined {
  const resolvedEngine = resolveEngine(engine);
  return cachedEngines.find((e) => e.engine === resolvedEngine)
    ?? BUILT_IN_ENGINES.find((e) => e.engine === resolvedEngine);
}

/** Whether the given engine has a job-scheduler provider. */
export function hasJobSchedulerSupport(
  engine: DatabaseEngineId | string | undefined,
): boolean {
  return getJobSchedulerEngineConfig(engine) !== undefined;
}

/** Whether any connected connection uses an engine with job-scheduler support. */
export function isAnyJobSchedulerEngineConnected(): boolean {
  return useConnectionStore.getState().activeConnections.some(
    (c) => c.status === 'connected' && hasJobSchedulerSupport(c.connection?.engine),
  );
}

/** All engine IDs that have job-scheduler support (for explorer gating). */
export function getJobSchedulerEngineIds(): readonly string[] {
  return Array.from(new Set([...BUILT_IN_ENGINES, ...cachedEngines].map((e) => e.engine)));
}

/** Toolbar label for the given engine (e.g. "Agent Jobs" for MSSQL). Falls back to "Job Scheduler". */
export function getJobSchedulerToolbarLabel(
  engine: DatabaseEngineId | string | undefined,
): string {
  return getJobSchedulerEngineConfig(engine)?.toolbarLabel ?? 'Job Scheduler';
}

function subscribeJobSchedulerEngineSupport(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getJobSchedulerSupportVersion(): number {
  return supportVersion;
}

export function useJobSchedulerEngineSupportVersion(): number {
  return useSyncExternalStore(
    subscribeJobSchedulerEngineSupport,
    getJobSchedulerSupportVersion,
    getJobSchedulerSupportVersion,
  );
}

/**
 * Test-only: replace the cached engines (avoids needing real IPC in unit tests).
 * @internal
 */
export function _testSetEngines(engines: readonly JobSchedulerSupportedEngine[]): void {
  initialized = true;
  setCachedEngines(engines);
}

/** Test-only: reset to uninitialized state. @internal */
export function _testReset(): void {
  initialized = false;
  setCachedEngines([]);
}
